import { Controller } from "@hotwired/stimulus"
import { buildChips, rangeText, summarize, urlWithout } from "./filter_chips_logic"

// Shows each applied filter as a chip, labelled exactly like its field in the filter form.
// Clicking a chip opens the filters; its x removes that filter.
export default class extends Controller {
  static values = { formId: String, removeLabel: String, openAction: String }
  static targets = ["list"]

  connect() {
    const form = document.getElementById(this.formIdValue)?.closest("form")
    const chips = buildChips(new URL(window.location.href).searchParams, name => this.describe(form, name))
    this.listTarget.replaceChildren(...chips.map(chip => this.chipElement(chip)))
    this.element.hidden = chips.length === 0
  }

  describe(form, name) {
    const input = form?.querySelector(`[name="${CSS.escape(name)}"]`)
    const group = input?.closest("[data-filter-group-label]")
    if (group) return this.describeGroup(form, group.dataset.filterGroupLabel)

    const wrapper = input?.closest("[data-field-label]")
    if (!wrapper) return null

    return { key: wrapper, label: wrapper.dataset.fieldLabel, display: this.displayValue(wrapper) }
  }

  // A from/to pair of separate fields reads as one range, e.g. "Position Fit: 50 → 80".
  describeGroup(form, label) {
    const items = form.querySelectorAll(`[data-filter-group-label="${CSS.escape(label)}"]`)
    const [from, to] = [...items].map(item => this.displayValue(item) || "")
    return { key: `group:${label}`, label, display: rangeText(from, to) }
  }

  // What the field shows: chosen option names, the formatted date, typed text, or nothing for a checkbox.
  displayValue(wrapper) {
    const select = wrapper.querySelector("select")
    if (select) return summarize([...select.selectedOptions].filter(option => option.value !== "").map(option => option.text.trim()))

    const visible = wrapper.querySelector("input:not([type=hidden]):not([type=checkbox])")
    if (visible) return visible.value
    return wrapper.querySelector("input[type=checkbox]") ? "" : undefined
  }

  chipElement(chip) {
    const element = document.createElement("span")
    element.className = "inline-flex max-w-full items-center rounded-full border border-gray-200 bg-white text-sm text-gray-700 shadow-xs"

    const open = document.createElement("button")
    open.type = "button"
    open.className = "min-w-0 truncate rounded-l-full py-1 pl-3 pr-1 text-left hover:text-gray-900 cursor-pointer"
    open.dataset.action = this.openActionValue
    const label = document.createElement("span")
    label.className = "font-medium"
    label.textContent = chip.text ? `${chip.label}: ` : chip.label
    open.append(label, chip.text)

    const remove = document.createElement("a")
    remove.href = urlWithout(window.location.href, chip.names)
    remove.className = "flex size-7 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-900"
    remove.setAttribute("aria-label", `${this.removeLabelValue}: ${chip.label}`)
    remove.textContent = "×"

    element.append(open, remove)
    return element
  }
}
